import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Switch,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Button
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { settingsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import EmailTemplateForm from './EmailTemplateForm'; 
import EmptyState from '../common/EmptyState';

const EmailTemplateList = ({ type }) => {
  const { id } = useParams();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showToast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [id, type]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      let response;
      
      if (type === 'course') {
        response = await settingsService.getCourseEmailTemplates(id);
      } else if (type === 'learningPath') {
        response = await settingsService.getLearningPathEmailTemplates(id);
      }
      
      if (response.data && response.data.templates) {
        setTemplates(response.data.templates);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching email templates:', err);
      setError('Failed to load email templates. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (templateId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const data = { id: templateId, status: newStatus };
      
      let response;
      if (type === 'course') {
        response = await settingsService.updateCourseEmailTemplateStatus(data);
      } else if (type === 'learningPath') {
        response = await settingsService.updateLearningPathEmailTemplateStatus(data);
      }
      
      if (response.data.success) {
        // Update local state
        setTemplates(templates.map(template => 
          template.id === templateId ? { ...template, status: newStatus } : template
        ));
        showToast('Email template status updated successfully','success');
      } else {
        showToast(response.data.message || 'Failed to update template status','error');
      }
    } catch (err) {
      console.error('Error updating template status:', err);
      showToast('Failed to update template status','error');
    }
  };

  const handleOpenForm = (template) => {
    setSelectedTemplate(template);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedTemplate(null);
  };

  const handleSaveTemplate = async (formData) => {
    try {
      const response = await settingsService.updateEmailTemplates(formData);
      
      if (response.data.success) {
        showToast('Template updated successfully','success');
        fetchTemplates(); 
        handleCloseForm();
      } else {
        showToast(response.data.message || 'Failed to update template','error');
      }
    } catch (err) {
      console.error('Error updating template:', err);
      showToast('Failed to update template','error');
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box my={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>      
      {templates.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Template</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <Button
                        onClick={() => handleOpenForm(template)}
                        >
                        {template.name}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Switch
                        checked={template.status === 1}
                        onChange={() => handleToggleStatus(template.id, template.status)}
                        color="primary"
                        inputProps={{ 'aria-label': 'toggle template status' }}
                      />
                      <Typography variant="body2" ml={1}>
                        {template.status === 1 ? 'Enabled' : 'Disabled'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <>
          {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14): a plain line. */}
          <EmptyState title="No email templates found." message={null} />
        </>
      )}

      <Dialog 
        open={formOpen} 
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Template: {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <EmailTemplateForm 
              template={selectedTemplate}
              onSave={handleSaveTemplate}
              onCancel={handleCloseForm}
            />
          )}
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default EmailTemplateList;